<i18n src="./translations"></i18n>

<template>
  <div
    v-if="!isOTCSalesChannel"
    class="availability"
    :class="[classes, category, { 'is-b2b': isBusinessCustomer }]"
    :data-preload="[$options.name]"
    data-testid="product-availability"
    :data-available="category !== 'delayed' ? 'true' : 'false'"
    :data-category="category"
    role="status"
    aria-live="polite"
  >
    <div class="label">
      <Icon
        class="icon"
        height="20"
        width="20"
        :name="iconName"
        aria-hidden="true"
      />
      <span>{{ $t(`pdp-availability.${status}.label`) }}</span>
    </div>

    <div v-if="showNote" class="note">
      {{ $t(`pdp-availability.${status}.text`) }}
    </div>
  </div>
</template>

<script>
import globalMixin from 'Libs/mixins/globalMixin';
import Icon from 'Components/00-generated/Icon';
import { mapState, mapGetters } from 'vuex';

// Status map
export const STATUS_SYNONYMS = Object.freeze({
  // Instant synonyms
  instant: 'instant',
  available: 'available_today',
  available_today: 'available_today',
  // Soon synonyms
  '2to3workdays': '2_to_3_workdays',
  '2_to_3_workdays': '2_to_3_workdays',
  '3to4workdays': 'delivery_within_3_5_working_days',
  '3to5workdays': 'delivery_within_3_5_working_days',
  '5to10workdays': '5_to_10_workdays',
  '5_to_10_workdays': '5_to_10_workdays',
  // Delayed synonyms
  '2to4weeks': 'delivery_within_2_4_weeks',
  delayed: 'delayed',
  available_soon: 'delayed',
  available_on_request: 'delayed',
  out_of_stock: 'out_of_stock',
  notavailable: 'not_available',
  not_available: 'not_available',
  unavailable: 'not_available',
});

// Status Category
const STATUS_INSTANT = new Set(['instant', 'available_today']);
export const STATUS_SOON = new Set([
  '2_to_3_workdays',
  'delivery_within_3_5_working_days',
  '5_to_10_workdays',
]);

export default {
  name: 'PdpAvailability',
  components: { Icon },
  mixins: [globalMixin],
  props: {
    state: {
      type: String,
      default: '',
      required: true,
    },
  },
  computed: {
    ...mapState('core', {
      promiseBanner: ({ promiseBanner }) => promiseBanner || {},
    }),
    ...mapGetters('core', {
      isBusinessCustomer: 'isBusinessCustomer',
      isOTCSalesChannel: 'isOTCSalesChannel',
    }),
    status() {
      let status = (this.state || '').toLowerCase();
      const isAvailableToday =
        this.promiseBanner.visible && status === 'instant';
      status = isAvailableToday ? 'available_today' : status;
      return STATUS_SYNONYMS[status] || 'not_available';
    },
    category() {
      if (STATUS_INSTANT.has(this.status)) return 'instant';
      if (STATUS_SOON.has(this.status)) return 'soon';
      return 'delayed';
    },
    iconName() {
      return this.category === 'instant' ? 'shipping' : 'delivery_standard';
    },
    showNote() {
      return this.status === 'available_today' && !this.isBusinessCustomer;
    },
  },
};
</script>

<style scoped lang="scss">
@import 'mixins';

.availability {
  display: inline-flex;
  flex-direction: column;

  --indicative-color: var(--color-nero);

  @include typography-size(var(--font-size-SM), 19px);

  .label {
    display: inline-flex;
    width: fit-content;
    color: var(--color-nero);
    line-height: 1.29;

    .icon {
      margin-right: var(--space-0--half);
      fill: var(--indicative-color);

      @include icon-dimensions(24px);
    }
  }

  .note {
    color: var(--color-nero);
    font-size: var(--font-size-SM);
    line-height: 1.29;
    margin-top: var(--space-0--half);
  }

  &.instant {
    --indicative-color: var(--color-japanese-laurel);

    .label {
      display: flex;
      align-items: center;
      color: var(--color-white);
      background: var(--color-japanese-laurel);
      padding: 0 var(--space-0--half);
      border-radius: var(--space-0--half);
      font-weight: bold;
      height: var(--space-3);

      .icon {
        fill: var(--color-white);
      }
    }
  }

  &.soon {
    --indicative-color: var(--color-japanese-laurel);

    &.is-b2b {
      --indicative-color: var(--color-24-blue);
    }
  }

  &.delayed {
    --indicative-color: var(--color-amber);
  }
}

:root[data-theme='kfzteile24'] {
  .availability {
    &.instant {
      .label {
        color: var(--color-secondary);
        background-color: var(--color-primary-light);

        .icon {
          fill: var(--color-secondary);
        }
      }
    }

    &.soon {
      .icon {
        fill: var(--color-kfzteile24-semantic-green);
      }

      &.is-b2b .icon {
        fill: var(--color-secondary);
      }
    }
  }
}
</style>
